// jQuery is available globally in WordPress
const $ = window.jQuery;

/**
 * Fetch centers near a given zip code using the WordPress REST API route.
 * @param {Object} params
 * @param {string} params.zip  - The ZIP code to search near (required).
 * @param {string} [params.brand=''] - Optional brand filter (default empty).
 * @param {string} [params.dist='4000'] - Optional distance (default 4000).
 * @returns {jqXHR} jQuery AJAX promise
 */
export function findCenters({ zip, brand = '', dist = '4000' }) {
  // Use the restUrl passed by wp_localize_script
  const baseUrl = IScheduleData.restUrl; // e.g. "https://dev.lasik.com/wp-json/ischedule/v1"

  return $.ajax({
    url: `${baseUrl}/findCenters`,
    method: 'GET',
    data: { zip, brand, dist },
  });
}

/**
 * Fetch time slots for a given center using the WordPress REST API route.
 * @param {string} centerCode  - The center code (e.g. "LVIWesterville").
 * @param {boolean} [isDev=false] - Whether to use dev environment logic on the server side.
 * @returns {jqXHR} jQuery AJAX promise
 */
export function times4Center(centerCode, isDev = false) {
  const baseUrl = IScheduleData.restUrl;

  return $.ajax({
    url: `${baseUrl}/times4Center`,
    method: 'GET',
    data: {
      centerCode: centerCode,
      isDev: isDev, // The server checks this param, if true => use dev endpoint
    },
  });
}

/**
 * Perform user lookup with client token using the WordPress REST API route.
 * @param {string} clientToken - The client token for user lookup.
 * @param {boolean} [isDev=false] - Whether to use dev environment logic on the server side.
 * @returns {jqXHR} jQuery AJAX promise
 */
export function loginHome(clientToken, isDev = false) {
  const baseUrl = IScheduleData.restUrl;

  return $.ajax({
    url: `${baseUrl}/loginHome`,
    method: 'GET',
    data: {
      clientToken: clientToken,
      isDev: isDev, // The server checks this param, if true => use dev endpoint
    },
  });
}

/**
 * Reschedule an appointment using the WordPress REST API route.
 * @param {Object} params - The reschedule parameters.
 * @param {string} params.centerCode - The center code.
 * @param {string} params.apptDate - The appointment date.
 * @param {string} params.apptTime - The appointment time.
 * @param {string} params.apptId - The appointment ID.
 * @param {string} [params.isVirtual] - Whether the appointment is virtual.
 * @param {string} params.clientToken - The client token.
 * @param {string} [params.brand] - The brand.
 * @param {string} [params.offerCode] - The offer summary/code.
 * @param {Array} [params.trackers] - Array of tracker values.
 * @param {boolean} [params.isDev=false] - Whether to use dev environment logic on the server side.
 * @param {string} [params.did] - LVI tracking parameter.
 * @param {string} [params.venid] - LVI tracking parameter.
 * @param {string} [params.subid] - LVI tracking parameter.
 * @param {string} [params.eventid] - TLC tracking parameter.
 * @param {string} [params.trans] - Search tracking parameter.
 * @returns {jqXHR} jQuery AJAX promise
 */
export function reschedule(params) {
  const baseUrl = IScheduleData.restUrl;
  const {
    centerCode,
    apptDate,
    apptTime,
    apptId,
    isVirtual,
    clientToken,
    brand,
    offerCode,
    trackers,
    isDev = false,
    company_url,
    IPaddress,
    did,
    venid,
    subid,
    eventid,
    trans,
  } = params;

  return $.ajax({
    url: `${baseUrl}/reschedule`,
    method: 'POST',
    contentType: 'application/json',
    data: JSON.stringify({
      centerCode,
      apptDate,
      apptTime,
      apptId,
      isVirtual,
      clientToken,
      brand,
      offerCode,
      trackers,
      isDev,
      company_url,
      IPaddress,
      did,
      venid,
      subid,
      eventid,
      trans,
    }),
  });
}

/**
 * Schedule a preop appointment using the WordPress REST API route.
 * @param {Object} params - The schedule preop parameters.
 * @param {string} params.centerCode - The center code.
 * @param {string} params.apptDate - The appointment date.
 * @param {string} params.apptTime - The appointment time.
 * @param {string} [params.isVirtual] - Whether the appointment is virtual.
 * @param {string} params.clientToken - The client token.
 * @param {string} [params.brand] - The brand.
 * @param {string} [params.offerCode] - The offer summary/code.
 * @param {Array} [params.trackers] - Array of tracker values.
 * @param {boolean} [params.isDev=false] - Whether to use dev environment logic on the server side.
 * @param {string} [params.did] - LVI tracking parameter.
 * @param {string} [params.venid] - LVI tracking parameter.
 * @param {string} [params.subid] - LVI tracking parameter.
 * @param {string} [params.eventid] - TLC tracking parameter.
 * @param {string} [params.trans] - Search tracking parameter.
 * @returns {jqXHR} jQuery AJAX promise
 */
export function schedulePreop(params) {
  const baseUrl = IScheduleData.restUrl;
  const {
    centerCode,
    apptDate,
    apptTime,
    isVirtual,
    clientToken,
    brand,
    offerCode,
    trackers,
    isDev = false,
    company_url,
    IPaddress,
    did,
    venid,
    subid,
    eventid,
    trans,
  } = params;

  return $.ajax({
    url: `${baseUrl}/schedulePreop`,
    method: 'POST',
    contentType: 'application/json',
    data: JSON.stringify({
      centerCode,
      apptDate,
      apptTime,
      isVirtual,
      clientToken,
      brand,
      offerCode,
      trackers,
      isDev,
      company_url,
      IPaddress,
      did,
      venid,
      subid,
      eventid,
      trans,
    }),
  });
}

/**
 * Book an appointment using the WordPress REST API route.
 * @param {Object} params - The book appointment parameters.
 * @param {string} params.centerCode - The center code.
 * @param {string} params.apptDate - The appointment date.
 * @param {string} params.apptTime - The appointment time.
 * @param {string} params.fname - First name.
 * @param {string} params.lname - Last name.
 * @param {string} params.email - Email address.
 * @param {string} params.dob - Date of birth.
 * @param {string} params.areaCode - Phone area code.
 * @param {string} params.phoneNo - Phone number.
 * @param {string} [params.isVirtual] - Whether the appointment is virtual.
 * @param {string} [params.encSource] - Encoded source.
 * @param {string} [params.smsOptin] - SMS opt-in preference.
 * @param {string} [params.zipCode] - Zip code.
 * @param {string} [params.brand] - The brand.
 * @param {string} [params.offerCode] - The offer summary/code.
 * @param {Array} [params.trackers] - Array of tracker values.
 * @param {boolean} [params.isDev=false] - Whether to use dev environment logic on the server side.
 * @param {string} [params.did] - LVI tracking parameter.
 * @param {string} [params.venid] - LVI tracking parameter.
 * @param {string} [params.subid] - LVI tracking parameter.
 * @param {string} [params.eventid] - TLC tracking parameter.
 * @param {string} [params.trans] - Search tracking parameter.
 * @returns {jqXHR} jQuery AJAX promise
 */
export function bookAppt(params) {
  const baseUrl = IScheduleData.restUrl;
  const {
    centerCode,
    apptDate,
    apptTime,
    fname,
    lname,
    email,
    dob,
    areaCode,
    phoneNo,
    isVirtual,
    encSource,
    smsOptin,
    zipCode,
    brand,
    offerCode,
    trackers,
    isDev = false,
    company_url,
    IPaddress,
    did,
    venid,
    subid,
    eventid,
    trans,
  } = params;

  return $.ajax({
    url: `${baseUrl}/bookAppt`,
    method: 'POST',
    contentType: 'application/json',
    data: JSON.stringify({
      centerCode,
      apptDate,
      apptTime,
      fname,
      lname,
      email,
      dob,
      areaCode,
      phoneNo,
      isVirtual,
      encSource,
      smsOptin,
      zipCode,
      brand,
      offerCode,
      trackers,
      isDev,
      company_url,
      IPaddress,
      did,
      venid,
      subid,
      eventid,
      trans,
    }),
  });
}

/**
 * Send preflight data using the WordPress REST API route.
 * @param {Object} params - The preflight parameters.
 * @param {string} params.fname - First name.
 * @param {string} params.lname - Last name.
 * @param {string} params.email - Email address.
 * @param {string} params.dob - Date of birth.
 * @param {string} params.areaCode - Phone area code.
 * @param {string} params.phoneNo - Phone number.
 * @param {string} params.centerCode - The center code.
 * @param {string} params.apptDate - The appointment date.
 * @param {string} params.apptTime - The appointment time.
 * @param {string} params.consultType - The consultation type. "in-person" or "virtual".
 * @param {string} params.zipPostal - Zip/postal code.
 * @param {string} params.brand - The brand.
 * @param {string} [params.encSource] - Encoded source.
 * @param {boolean} [params.isDev=false] - Whether to use dev environment logic on the server side.
 * @returns {jqXHR} jQuery AJAX promise
 */
export function preflight(params) {
  const baseUrl = IScheduleData.restUrl;
  const {
    fname,
    lname,
    email,
    dob,
    areaCode,
    phoneNo,
    centerCode,
    apptDate,
    apptTime,
    consultType,
    zipPostal,
    brand,
    encSource,
    isDev = false,
  } = params;

  return $.ajax({
    url: `${baseUrl}/preflight`,
    method: 'POST',
    data: {
      fname,
      lname,
      email,
      dob,
      areaCode,
      phoneNo,
      centerCode,
      apptDate,
      apptTime,
      consultType,
      zipPostal,
      brand,
      encSource,
      isDev,
    },
  });
}

/**
 * Verify an email address using the WordPress REST API route.
 * @param {string} email - The email address to verify.
 * @returns {jqXHR} jQuery AJAX promise
 */
export function verifyEmail(email) {
  const baseUrl = IScheduleData.restUrl;

  return $.ajax({
    url: `${baseUrl}/verifyEmail`,
    method: 'GET',
    data: { email },
  });
}

/**
 * Verify a phone number using the WordPress REST API route.
 * @param {string} phone - The phone number to verify (digits only).
 * @returns {jqXHR} jQuery AJAX promise
 */
export function verifyPhone(phone) {
  const baseUrl = IScheduleData.restUrl;

  return $.ajax({
    url: `${baseUrl}/verifyPhone`,
    method: 'GET',
    data: { phone },
  });
}
